import { ChevronLeft, ChevronRight } from "lucide-react";
import PennText from "@/components/ui/text/PennText";

type TablePaginationProps = {
  /** Zero-based current page index. */
  pageIndex: number;
  /** Rows requested per page. */
  pageSize: number;
  /** Number of rows actually returned for the current page. */
  rowsOnPage: number;
  /** Whether a next page likely exists (current page came back full). */
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onPageSizeChange: (size: number) => void;
  /** Dim the controls while a page is being fetched. */
  busy?: boolean;
  pageSizeOptions?: number[];
};

/**
 * Offset-based pagination control. The list endpoints in this app return a
 * plain array with no total count, so we cannot show "page X of N" — instead
 * we show the current range and enable "Next" only when the page came back
 * full. Reusable across any AdminTable-backed page.
 */
export function TablePagination({
  pageIndex,
  pageSize,
  rowsOnPage,
  hasNext,
  onPrev,
  onNext,
  onPageSizeChange,
  busy = false,
  pageSizeOptions = [10, 20, 50, 100],
}: TablePaginationProps) {
  const start = rowsOnPage === 0 ? 0 : pageIndex * pageSize + 1;
  const end = pageIndex * pageSize + rowsOnPage;

  const btn =
    "inline-flex items-center gap-1 px-3 py-2 text-button rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-blue_grey2 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
      <div className="flex items-center gap-2">
        <PennText as="span" size="xs" color="on-navy-secondary">
          {start === 0 ? "No results" : `Showing ${start}–${end}`}
        </PennText>
        <span className="text-on-navy-tertiary">·</span>
        <label className="flex items-center gap-2">
          <PennText as="span" size="xs" color="on-navy-secondary">
            Rows
          </PennText>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-blue_grey2 px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-penn-100 text-small"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btn}
          onClick={onPrev}
          disabled={busy || pageIndex === 0}
        >
          <ChevronLeft size={14} />
          Prev
        </button>
        <PennText as="span" size="xs" color="on-navy-secondary" className="min-w-[3rem] text-center">
          Page {pageIndex + 1}
        </PennText>
        <button
          type="button"
          className={btn}
          onClick={onNext}
          disabled={busy || !hasNext}
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
